<script>
  import { _ } from "svelte-i18n"
  import { createEventDispatcher } from 'svelte'
  import { addOrMergeFontObjects, processFontFile } from '../services/font_files'
  import { uploadAllFonts } from '../services/font_file_upload'
  import { saveStorefrontData, reloadUserStorefronts } from '../services/storefronts'
  import { reloadTagedFonts } from '../services/tags';
  import { handleUpsell, resetAllFilters } from '../modules/helpers'
  import { detected_fonts, system_fonts, dropzone_visible, flash_content, previews_nav_filters, user_storefronts, previews_storefronts_filter, dropped_font_files, font_upload_progress } from '../stores'
  import Button from '../elements/Button.svelte'
  import Blink from '../elements/Blink.svelte'
  import TimesButton from '../elements/TimesButton.svelte'

  const dispatch = createEventDispatcher()

  export let inputOnly = false;
  export let filesOnly = false;
  export let onWarning = false;
  export let accentButton = false;
  
  let fileInput;
  let highlight = false;
  let dropZoneElement;
  let loading = false;
  let processedFontObjects = [];
  let collectedFontFiles = [];
  
  // Important, we need to deduct the intent of the dropzone
  $: intent = $previews_nav_filters.storefront || $previews_nav_filters.uploaded ? 'upload' : 'preview';
  $: filteredStorefront = $previews_storefronts_filter 
    ? $user_storefronts.find(storefront => storefront.id == $previews_storefronts_filter) 
    : null;

  
  // -------------------------
  // Main Processing Functions
  // -------------------------

  const handleDrop = async (items) => {
    // Show loading message
    loading = true;
    console.log('Dropzone - Handling dropped items:', items);

    // Reset arrays
    processedFontObjects = [];
    collectedFontFiles = [];

    // Traverse the dropped items with promises
    const traversePromises = [];
    
    // Handle FileList from input element
    if (items instanceof FileList || Array.isArray(items)) {
      for (let item of items) {
        const extension = item.name.split('.').pop().toLowerCase();
        if (extension === 'ttf' || extension === 'otf') {
          collectedFontFiles = [...collectedFontFiles, item];
        }
      }
      traversePromises.push(Promise.resolve());
    } 
    // Handle drag and drop DataTransfer items
    else {
      for (let i = 0; i < items.length; i++) {
        if (items[i].webkitGetAsEntry) {
          const item = items[i].webkitGetAsEntry();
          if (item) {
            traversePromises.push(collectFontFiles(item));
          }
        }
      }
    }

    try {
      // Wait for all file traversePromises to resolve
      await Promise.all(traversePromises);
      
      console.log('Dropzone - Files collected:', collectedFontFiles);
      
      // All files have been collected, filter duplicate files prefering otf over ttfs
      collectedFontFiles = collectedFontFiles.filter((file, index, self) => {
        const baseName = file.name.substr(0, file.name.lastIndexOf('.'));
        const otfFile = self.find(f => f.name === baseName + '.otf');
        return file.name.endsWith('.otf') || !otfFile;
      });
      
      // If no font files were collected, stop processing
      if (collectedFontFiles.length === 0) {
        loading = false;
        $dropzone_visible = false;
        return;
      }

      console.log('Dropzone - Starting to process font files', collectedFontFiles.length);
      
      // Process each font file with promises
      // `resultsOfProcessFontFile` will be an array of fontObjects or nulls,
      // maintaining the order of `collectedFontFiles`.
      const resultsOfProcessFontFile = await Promise.all(
        collectedFontFiles.map(async (file, idx) => { // Use idx to avoid conflict with outer scope index if any
          // Calculate font ID. This ID generation might need review if not all processed fonts are added to detected_fonts
          // or if `addOrMergeFontObjects` handles ID conflicts robustly.
          // For now, assumes $detected_fonts.length is a reasonable base.
          const fontId = $detected_fonts.length > 0 ? Math.max(...$detected_fonts.map(f => f.id)) + 1 : 0;
          const fontObject = await processFontFile(file, 'dropped', fontId ); // Source should be 'dropped' here, will be 'uploaded' when only available as an uploaded font
          return fontObject; // This will be the fontObject or null
        })
      );
      
      // Filter out nulls to get successfully processed font objects for local operations
      // The global/component `processedFontObjects` will store only successfully processed ones.
      processedFontObjects = resultsOfProcessFontFile.filter(obj => obj !== null);
      
      console.log('Dropzone - Font files processed (successfully):', processedFontObjects.length);
      
      // Add successfully processed fonts into detected_fonts or merge properties if already exists
      addOrMergeFontObjects(processedFontObjects);
           
      // Reload tagged fonts to get tags for dropped fonts
      reloadTagedFonts();

      // Apply intent when all fonts processed
      if (intent === 'upload') {
        const fontEntries = [];
        collectedFontFiles.forEach((file, index) => {
          const processedResult = resultsOfProcessFontFile[index]; // Get the result for the current file
          if (processedResult) { // Only include if successfully processed locally
            fontEntries.push({
              file: file, // The original File object
              metadata: {
                postscript_name: processedResult.postscript_name,
                display_name: processedResult.display_name // Directly use display_name from processedResult
              }
            });
          }
        });

        if (fontEntries.length > 0) {
          console.log('Dropzone - Uploading fonts via uploadAllFonts:', fontEntries);
          const uploadResult = await uploadAllFonts(fontEntries);

          if (uploadResult.data.success && uploadResult.data.fonts) {
            // Update detected_fonts with server-confirmed filenames and uploaded status
            const successfullyUploadedMap = new Map();
            uploadResult.data.fonts.forEach(uploadedFont => {
              successfullyUploadedMap.set(uploadedFont.postscript_name, uploadedFont.filename);
            });
            detected_fonts.update(fonts => 
              fonts.map(font => {
                if (successfullyUploadedMap.has(font.postscript_name)) {
                  return { 
                    ...font, 
                    file_name: successfullyUploadedMap.get(font.postscript_name), 
                    uploaded: true
                  };
                }
                return font;
              })
            );
            
            $flash_content = { message: $_('flashes.fontsUploaded', { values: { count: uploadResult.data.fonts.length } }), bright: true };

            // If a storefront was targeted, add these uploaded fonts to it.
            if (filteredStorefront && uploadResult.data.fonts.length > 0) {
              const fontsToAdd = uploadResult.data.fonts.map(uploadedFont => {
                return {
                  postscript_name: uploadedFont.postscript_name,
                  preferred_name: uploadedFont.display_name, 
                  file_name: uploadedFont.filename 
                };
              });

              const storefrontUpdateResponse = await saveStorefrontData({
                storefront_id: filteredStorefront.id,
                fonts_to_add: fontsToAdd,
              });
              if (storefrontUpdateResponse.data.success) {
                $flash_content = { message: $_('flashes.storefrontFontsAdded', { values: { count: fontsToAdd.length, storefront_name: filteredStorefront.name } }), bright: true };
                reloadUserStorefronts(); // Refresh storefronts list
              } else {
                $flash_content = { message: $_('flashes.storefrontUpdateError'), bright: false }; // Use a different error for storefront update
              }
            }
          } else {
            // Handle upload failure
            const errorMessages = uploadResult.data.upload_errors.join('; ');
            $flash_content = { message: $_('flashes.fontsUploadError', { values: { error: errorMessages || 'Unknown error' } }), bright: false };
          }
        } else {
          $flash_content = { message: $_('flashes.noFontsToUpload'), bright: true };
        }
      } else {
        // keep font files in dropped_font_files store to use for glyphs access later
        $dropped_font_files = [...$dropped_font_files, ...collectedFontFiles];
        // Show flash message
        $flash_content = $_('flashes.droppedFonts', { values: { count: processedFontObjects.length } });
        
        // Go to the dropped fonts
        resetAllFilters();
        previews_nav_filters.filterDropped();

        // Upsell feature
        handleUpsell('fontdrop', false, 10000);
      }

      // Hide loading message
      loading = false;
      
      // Hide dropzone
      $dropzone_visible = false;

    } catch (error) {
      console.error('Dropzone - Error in handleDrop:', error);
      loading = false;
      $dropzone_visible = false;
      $flash_content = { message: $_('flashes.fontsUploadError', { values: { error: error.message || 'Unknown error' } }), bright: false };
    }
  }

  // ------------------------
  // File Processing Functions
  // ------------------------
  const collectFontFiles = (item, path = '') => { // was traverseFileTree
    return new Promise(resolve => {
      if (item.isFile) {
        item.file(file => {
          const fileExtension = file.name.split('.').pop().toLowerCase();
          if (fileExtension === 'ttf' || fileExtension === 'otf') {
            collectedFontFiles = [...collectedFontFiles, file];
          }
          resolve();
        });
      } else if (item.isDirectory) {
        const dirReader = item.createReader();
        dirReader.readEntries(entries => {
          const promises = [];
          for (let i = 0; i < entries.length; i++) {
            promises.push(collectFontFiles(entries[i], path + item.name + "/"));
          }
          Promise.all(promises).then(resolve);
        });
      }
    });
  }

  // Share boolean loading state with parent component
  $: dispatch('uploading', loading)

  // ------------------------
  // UI Event Handlers
  // ------------------------
  const onDragEnter = (e) => {
    preventDefaults(e);
    highlight = true;
  }

  const onDragLeave = (e) => {
    preventDefaults(e);
    highlight = false;
    $dropzone_visible = false;
  }

  function onFileSelect(event) {
    const files = Array.from(event.target.files);
    handleDrop(files);
  }

  const onDrop = (e) => {
    preventDefaults(e);
    let items = e.dataTransfer.items;
    handleDrop(items);
  }

  // ------------------------
  // Utility Functions
  // ------------------------
  const preventDefaults = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

</script>

{#if inputOnly}
  {#if loading}
    <Blink>
      <p class="loadingText">
        {#if $font_upload_progress?.uploaded !== undefined}
          Uploaded {$font_upload_progress.uploaded} of {$font_upload_progress.total} fonts...
        {:else}
          Loading {processedFontObjects?.length} of {collectedFontFiles?.length} fonts...
        {/if}
      </p>
    </Blink>
  {:else}  
    <Button size="small" kind={accentButton ? "accent" : "outline"} on_warning={onWarning} on:click={() => fileInput.click()}>
      <slot>or choose from your computer</slot>
    </Button>    
  {/if}
  <input 
    type="file" 
    bind:this={fileInput} 
    on:change={onFileSelect}
    style="display: none;" 
    multiple     
    {...(!filesOnly ? { webkitdirectory: 'true' } : {})}
    accept=".ttf,.otf" />
{:else if $dropzone_visible}
  <div 
  class="Dropzone"
  class:highlight
  class:inputOnly
  bind:this={dropZoneElement}
  on:dragenter="{onDragEnter}"
  on:dragover="{onDragEnter}"
  on:dragleave="{onDragLeave}"
  on:drop="{onDrop}"
  >
    <div class="inner">
      {#if loading}
        <Blink>
          {#if $font_upload_progress?.uploaded !== undefined}
            Uploaded {$font_upload_progress.uploaded} of {$font_upload_progress.total} fonts...
          {:else}
            Loading {processedFontObjects?.length} of {collectedFontFiles?.length} fonts...
          {/if}
        </Blink>
      {:else}
        {#if intent === 'upload' && !!filteredStorefront}
          Drop font files or folders to upload to the storefront
        {:else if intent === 'upload'}
          Drop font files or folders to upload
        {:else}
          Drop font files or folders to preview them
        {/if}
      {/if}
    </div>
    <div class="close">
      <TimesButton on:click={onDragLeave}/>
    </div>
  </div>
{/if}

<style>
  .Dropzone {
    position: fixed;
    z-index: 100;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    padding: 2rem;
    font-size: 2.2rem;
    color: var(--c-text);
    background: var(--c-bg);
    opacity: 0.85;
  }

  .Dropzone .inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border: 2px dashed var(--c-text-faded);
  }
  
  .Dropzone.highlight .inner {
    border-color: var(--c-accent);
    color: var(--c-accent);
  }

  .loadingText {
    display: inline-block;
    font-size: 1.5rem;
    font-weight: bold;
    color: var(--c-text);
    line-height: 3rem;
  }
  .close {
    position: absolute;
    top: 0.5rem;
    right: 1rem;
  }
</style>